import React, { useState } from 'react';
import { AlertTriangle, X, Eye, EyeOff } from 'lucide-react';
import { cn } from '../utils/cn';
import { authApi } from '../api/client';

interface DeleteSiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  siteName: string;
  errorMessage?: string | null;
}

export const DeleteSiteModal: React.FC<DeleteSiteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  siteName,
  errorMessage = null,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading && !verifying) {
      handleClose();
    }
  };

  const handleClose = () => {
    setPassword('');
    setPasswordError(null);
    setShowPassword(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setPasswordError('Password is required');
      return;
    }

    setPasswordError(null);
    setVerifying(true);

    try {
      await authApi.verifyPassword(password);
      // Password verified, proceed with deletion
      onConfirm();
    } catch (error: any) {
      if (error.status === 401) {
        setPasswordError('Incorrect password. Please try again.');
      } else {
        setPasswordError('Failed to verify password. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  };

  if (!isOpen) return null;

  const isProcessing = isLoading || verifying;

  return (
    <div
      className={cn(
        'modal-backdrop',
        'flex items-center justify-center z-50 p-4',
        isOpen ? 'animate-fade-in' : ''
      )}
      onClick={handleBackdropClick}
    >
      <div
        className={cn('modal-content', 'max-w-md w-full', isOpen ? 'animate-slide-up' : '')}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        {/* Modal Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
            </div>
            <div>
              <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
                Delete Website
              </h2>
              <p className="text-sm text-gray-500 mt-1">This action cannot be undone</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className={cn(
              'p-2 text-gray-400 hover:text-gray-600 rounded-lg',
              'hover:bg-gray-100 transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
            )}
            disabled={isProcessing}
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <p id="modal-description" className="text-gray-600 leading-relaxed mb-4">
              Are you sure you want to delete "<span className="font-semibold">{siteName}</span>"?
              This will permanently remove the website and all its pages.
            </p>

            <p className="text-gray-600 leading-relaxed mb-4">
              To confirm, please enter your password:
            </p>

            {/* Password Input */}
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  setPasswordError(null);
                }}
                placeholder="Enter your password"
                className={cn(
                  'w-full px-4 py-3 pr-12 border rounded-lg',
                  'focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent',
                  'transition-all duration-200',
                  passwordError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                )}
                disabled={isProcessing}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={isProcessing}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Password Error */}
            {passwordError && (
              <div className="mt-2 flex items-center space-x-2 text-red-600">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <p className="text-sm">{passwordError}</p>
              </div>
            )}

            {/* General Error Message */}
            {errorMessage && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={cn(
                'inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold',
                'bg-gradient-to-r from-red-600 to-red-700 text-white',
                'rounded-lg border border-transparent shadow-sm',
                'hover:from-red-700 hover:to-red-800 hover:shadow-md',
                'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                'transition-all duration-200 ease-in-out press-scale',
                isProcessing && 'opacity-50 cursor-not-allowed'
              )}
              disabled={isProcessing}
            >
              {verifying ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Verifying...</span>
                </div>
              ) : isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Deleting...</span>
                </div>
              ) : (
                'Delete Website'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
